import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Shield } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MODULES = [
  { id: 'Dashboard', name: 'Dashboard' },
  { id: 'Orders', name: 'Orders & Bookings' },
  { id: 'Approvals', name: 'Approvals' },
  { id: 'Kitty', name: 'Kitty Schemes' },
  { id: 'Customers', name: 'Customers' },
  { id: 'Vendors', name: 'Vendors' },
  { id: 'Reports', name: 'Reports' },
  { id: 'Settings', name: 'Settings' },
];

const RolesManagement = ({ user }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    role_name: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/roles`);
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRole(null);
    setFormData({
      role_name: '',
      description: '',
      permissions: MODULES.map(m => ({
        module: m.id,
        can_view: false,
        can_create: false,
        can_edit: false,
        can_delete: false,
        can_approve: false
      }))
    });
    setShowModal(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      role_name: role.role_name,
      description: role.description || '',
      permissions: role.permissions && role.permissions.length > 0 
        ? role.permissions 
        : MODULES.map(m => ({
            module: m.id,
            can_view: false,
            can_create: false,
            can_edit: false,
            can_delete: false,
            can_approve: false
          }))
    });
    setShowModal(true);
  };

  const handlePermissionChange = (moduleIndex, permission, value) => {
    const newPermissions = [...formData.permissions];
    newPermissions[moduleIndex] = {
      ...newPermissions[moduleIndex],
      [permission]: value
    };
    setFormData({ ...formData, permissions: newPermissions });
  };

  const handleSave = async () => {
    try {
      if (!formData.role_name.trim()) {
        toast.error('Role name is required');
        return;
      }

      const payload = {
        role_name: formData.role_name.trim(),
        description: formData.description.trim(),
        permissions: formData.permissions,
        created_by: user?.username || 'admin',
        is_active: true
      };

      let response;
      if (editingRole) {
        payload.role_id = editingRole.role_id;
        response = await fetch(`${BACKEND_URL}/api/roles/${editingRole.role_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${BACKEND_URL}/api/roles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        toast.success(editingRole ? 'Role updated successfully' : 'Role created successfully');
        setShowModal(false);
        fetchRoles();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save role');
      }
    } catch (error) {
      console.error('Error saving role:', error);
      toast.error('Failed to save role');
    }
  };

  const handleDelete = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/roles/${roleId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Role deleted successfully');
        fetchRoles();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete role');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error('Failed to delete role');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Roles & Permissions</h2>
          <p className="text-sm text-gray-600">Manage user roles and module access</p>
        </div>
        <button
          onClick={handleAdd}
          className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Role
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map((role) => (
            <div key={role.role_id} className="bg-white p-4 rounded-lg shadow border-2 border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-600" />
                  <h3 className="font-bold text-lg">{role.role_name}</h3>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(role)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <Edit2 className="w-4 h-4 text-blue-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(role.role_id)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
              {role.description && (
                <p className="text-sm text-gray-600 mb-3">{role.description}</p>
              )}
              <div className="text-xs text-gray-500">
                {role.permissions?.length || 0} module permissions
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-xl font-bold">
                {editingRole ? 'Edit Role' : 'Add New Role'}
              </h3>
              <button onClick={() => setShowModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Role Name *</label>
                <input
                  type="text"
                  value={formData.role_name}
                  onChange={(e) => setFormData({ ...formData, role_name: e.target.value })}
                  className="w-full border rounded-lg p-2"
                  placeholder="e.g., Sales Manager"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border rounded-lg p-2"
                  rows="2"
                  placeholder="Brief description of the role"
                />
              </div>

              <div>
                <h4 className="font-bold text-lg mb-3">Module Permissions</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-2 text-left">Module</th>
                        <th className="p-2 text-center">View</th>
                        <th className="p-2 text-center">Create</th>
                        <th className="p-2 text-center">Edit</th>
                        <th className="p-2 text-center">Delete</th>
                        <th className="p-2 text-center">Approve</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MODULES.map((module, idx) => {
                        const permission = formData.permissions[idx] || {
                          module: module.id,
                          can_view: false,
                          can_create: false,
                          can_edit: false,
                          can_delete: false,
                          can_approve: false
                        };
                        return (
                          <tr key={module.id} className="border-b">
                            <td className="p-2 font-medium">{module.name}</td>
                            <td className="p-2 text-center">
                              <input
                                type="checkbox"
                                checked={permission.can_view}
                                onChange={(e) => handlePermissionChange(idx, 'can_view', e.target.checked)}
                              />
                            </td>
                            <td className="p-2 text-center">
                              <input
                                type="checkbox"
                                checked={permission.can_create}
                                onChange={(e) => handlePermissionChange(idx, 'can_create', e.target.checked)}
                              />
                            </td>
                            <td className="p-2 text-center">
                              <input
                                type="checkbox"
                                checked={permission.can_edit}
                                onChange={(e) => handlePermissionChange(idx, 'can_edit', e.target.checked)}
                              />
                            </td>
                            <td className="p-2 text-center">
                              <input
                                type="checkbox"
                                checked={permission.can_delete}
                                onChange={(e) => handlePermissionChange(idx, 'can_delete', e.target.checked)}
                              />
                            </td>
                            <td className="p-2 text-center">
                              <input
                                type="checkbox"
                                checked={permission.can_approve}
                                onChange={(e) => handlePermissionChange(idx, 'can_approve', e.target.checked)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Role
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesManagement;
